import Markdown from '@/components/ui/Markdown';
import getHelpCenterArticle from '@/components/features/help-center/getHelpCenterArticle';
import helpCenterArticlesConfig from '@/components/features/help-center/helpCenterArticlesConfig';
import {
  Box,
  Button,
  Card,
  Menu,
  MenuItem,
  MenuList,
  Paper,
  Typography,
} from '@mui/material';
import { PropsWithChildren, useState } from 'react';
import { GetServerSideProps } from 'next';
import LocalizedLink from '@/components/ui/LocalizedLink';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { dehydrate, useHydrate, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/reactQueryKeys/queryClient';
import CatchError from '@/components/system/CacthError';
import * as Sentry from '@sentry/nextjs';
import { useTranslation } from 'next-i18next';

export interface PolicyProps {
  dehydratedState: any;
}

// export const runtime = 'experimental-edge';

export const PoliciesPage = (props: PropsWithChildren<PolicyProps>) => {
  const { dehydratedState } = props;
  const { i18n } = useTranslation();
  const { asPath } = useRouter();
  const [value, setValue] = useState(0);

  useHydrate(dehydratedState);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(1);
  const open = Boolean(anchorEl);

  const articleExist = Object.keys(helpCenterArticlesConfig).find(
    (item) => helpCenterArticlesConfig[item].href === asPath,
  );

  const articleConfig = helpCenterArticlesConfig[articleExist];

  const handleClickListItem = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  let articleSlug = articleConfig?.slug;
  if (i18n.language && articleConfig.i18nSupport) {
    articleSlug += `-${i18n.language}`;
  }
  const queryKey = ['policies', articleSlug];

  const { data, error } = useQuery({
    queryKey: queryKey,
    cacheTime: Infinity,
    keepPreviousData: true,
    queryFn: async () => await getHelpCenterArticle({ articleSlug }),
  });

  const article = data?.data || null;

  if (!articleExist || !articleConfig || !article?.content) {
    return null;
  }

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLElement>,
    index: number,
  ) => {
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const content = article.content.replace(/<br>/gi, '\n');

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'row',
        [theme.breakpoints.down('lg')]: {
          flexDirection: 'column',
        },
      })}
    >
      <Box sx={{ width: 320, mr: [0, 1] }}>
        <Box
          sx={(theme) => ({
            [theme.breakpoints.up('lg')]: {
              display: 'none',
            },
            mb: 2,
          })}
        >
          <Button
            id="lock-button"
            aria-haspopup="listbox"
            aria-controls="lock-menu"
            variant="contained"
            color={'secondary'}
            onClick={handleClickListItem}
            endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          >
            {article.title}
          </Button>
          <Menu
            id="lock-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'lock-button',
              role: 'listbox',
            }}
          >
            {Object.keys(helpCenterArticlesConfig).map((key, index) => {
              const { slug, title, href } = helpCenterArticlesConfig[key];
              return (
                <LocalizedLink key={index} href={href}>
                  <MenuItem
                    onClick={(event) => handleMenuItemClick(event, index)}
                    selected={articleConfig.slug === slug}
                  >
                    {title}
                  </MenuItem>
                </LocalizedLink>
              );
            })}
          </Menu>
        </Box>
        <Card
          sx={(theme) => ({
            padding: theme.spacing(1, 2),
            [theme.breakpoints.down('lg')]: {
              display: 'none',
            },
          })}
        >
          <MenuList>
            {Object.keys(helpCenterArticlesConfig).map((key, index) => {
              const { slug, title, href } = helpCenterArticlesConfig[key];
              return (
                <LocalizedLink key={index} href={href}>
                  <MenuItem selected={articleConfig.slug === slug}>
                    {title}
                  </MenuItem>
                </LocalizedLink>
              );
            })}
          </MenuList>
        </Card>
      </Box>
      <Paper>
        <Box ml={[1, 2, 3]} mr={[1, 2, 3]} mt={[1, 2, 1]} mb={[1, 2, 3]}>
          <Typography variant="h6">{article.title}</Typography>
          {content && <Markdown key={article.id}>{content}</Markdown>}
        </Box>
      </Paper>
    </Box>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { resolvedUrl, locale } = context;
  if (!resolvedUrl) {
    return {
      redirect: {
        permanent: false,
        destination: helpCenterArticlesConfig.terms.href,
      },
    };
  }

  const articleExist = Object.keys(helpCenterArticlesConfig).find(
    (item) => helpCenterArticlesConfig[item].href === resolvedUrl,
  );

  if (!articleExist) {
    return {
      notFound: true,
    };
  }

  let articleSlug = helpCenterArticlesConfig[articleExist].slug;
  if (locale && helpCenterArticlesConfig[articleExist].i18nSupport) {
    articleSlug += `-${locale}`;
  }
  const key = ['policies', articleSlug];

  try {
    await queryClient.prefetchQuery(key, () => {
      return getHelpCenterArticle({
        articleSlug,
      });
    });

    const dehydratedState = dehydrate(queryClient);

    return {
      props: {
        dehydratedState,
        ...(await serverSideTranslations(locale, ['common'])),
      },
    };
  } catch (e) {
    console.error(e);
    Sentry.captureException(e);
    return {
      notFound: true,
    };
  }
};

export default CatchError(PoliciesPage);
